import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Grid, Typography, Paper, LinearProgress, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const AboutSection = () => {
  const items = [
    {
      title: 'Rapid Response',
      desc: 'We act fast to stop water damage before it spreads and causes costly structural issues.'
    },
    {
      title: 'Expert Care',
      desc: 'Certified technicians handle every restoration with precision, safety, and care.'
    },
    {
      title: 'Proven Quality',
      desc: 'Advanced drying equipment and proven methods ensure complete water removal.'
    },
    {
      title: 'People First',
      desc: 'We support homeowners through every step, including insurance coordination.'
    }
  ];

  const colA = items.slice(0, 2);
  const colB = items.slice(2, 4);

  const BG = '#F9F7F3';
  const GREEN_96 = '#43934A';
  

  const counterWrapRef = useRef(null);
  const playedRef = useRef(false);

  const DIGIT_H = 76;
  const DIGIT_W = 34;
  const DIGIT_FS = 58;
  const SPINS = 4;
  const VALUE = 99;

  const digits = useMemo(() => String(VALUE).split('').map((ch) => Number(ch)), []);
  const reelRefs = useRef([]);
  const percentReelRef = useRef(null);

  useEffect(() => {
    const el = counterWrapRef.current;
    if (!el) return;

    const prefersReduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting) return;
        if (playedRef.current) return;

        playedRef.current = true;
        if (prefersReduced) return;

        setTimeout(() => {
          reelRefs.current.forEach((reelEl, idx) => {
            if (!reelEl) return;

            const targetDigit = digits[idx];
            const targetIndex = SPINS * 10 + targetDigit;
            const distance = targetIndex * DIGIT_H;

            reelEl.style.transform = 'translateY(0px)';

            reelEl.animate(
              [{ transform: 'translateY(0px)' }, { transform: `translateY(-${distance}px)` }],
              {
                duration: 6500,
                delay: idx * 650,
                easing: 'cubic-bezier(.08,.85,.12,1)',
                fill: 'forwards'
              }
            );
          });

          if (percentReelRef.current) {
            const percentEl = percentReelRef.current;

            const totalSteps = (SPINS + 1) * 10;
            const targetIndex = totalSteps - 1;
            const distance = targetIndex * DIGIT_H;

            percentEl.style.transform = 'translateY(0px)';

            percentEl.animate(
              [{ transform: 'translateY(0px)' }, { transform: `translateY(-${distance}px)` }],
              {
                duration: 6200,
                delay: digits.length * 650 + 200,
                easing: 'cubic-bezier(.08,.85,.12,1)',
                fill: 'forwards'
              }
            );
          }
        }, 400);

        observer.disconnect();
      },
      {
        threshold: 0.9,
        rootMargin: '0px 0px -15% 0px'
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [digits]);

  const ReelDigit = ({ setRef }) => {
    const totalSteps = (SPINS + 1) * 10;

    return (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: `${DIGIT_H}px`,
          width: `${DIGIT_W}px`,
          display: 'block'
        }}
      >
        <Box ref={setRef} sx={{ position: 'absolute', top: 0, left: 0, transform: 'translateY(0px)' }}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                height: `${DIGIT_H}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                sx={{
                  fontSize: `${DIGIT_FS}px`,
                  fontWeight: 800,
                  lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                  color: GREEN_96
                }}
              >
                {i % 10}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const ReelPercent = () => {
    const totalSteps = (SPINS + 1) * 10;

    return (
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          height: `${DIGIT_H}px`,
          width: '28px',
          display: 'block'
        }}
      >
        <Box ref={percentReelRef} sx={{ position: 'absolute', top: 0, left: 0, transform: 'translateY(0px)' }}>
          {Array.from({ length: totalSteps }).map((_, i) => (
            <Box
              key={i}
              sx={{
                height: `${DIGIT_H}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ fontSize: '30px', fontWeight: 800, lineHeight: 1, color: GREEN_96 }}>
                {i === totalSteps - 1 ? '%' : '\u00A0'}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const ServiceBlock = ({ title, desc }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckCircleIcon sx={{ color: GREEN_96, fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {desc}
      </Typography>
    </Box>
  );

  return (
    <Box id="about" sx={{ py: { xs: 8, md: 10 }, bgcolor: BG }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        <Grid container>
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                className="bt_bb_handwritten_fonts bt_bb_size_normal bt_bb_headline bt_bb_headline_superheadline supertitle-font"
                sx={{
              fontFamily: '"Caveat", cursive',
              color: '#ff7f00',
              fontSize: { xs: 22, md: 26 },
              fontWeight: 700,
              mb: 1
            }}
              >
                About us
              </Typography>

              <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 2 }}>
                Expert Water Damage Restoration in Bloomington, IN
              </Typography>

              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  whiteSpace: 'pre-line',
                  maxWidth: 900,
                  mx: 'auto'
                }}
              >
              <b>24/7 Emergency Response. From Extraction to Restoration, We Make It Right.</b> <br/>
                When water damage strikes, every minute counts. Rite Water Remediation 
provides rapid, professional water extraction, structural drying, and damage repair for 
homes and businesses across Bloomington and Monroe County. Don’t let a leak become a 
disaster. </Typography>
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={6} sx={{ mt: { xs: 4, md: 6 } }} alignItems="flex-start">
          <Grid item xs={12} lg={3}>
            <Stack spacing={3}>
              <ServiceBlock title={colA[0].title} desc={colA[0].desc} />
              <ServiceBlock title={colA[1].title} desc={colA[1].desc} />
            </Stack>
          </Grid>

          <Grid item xs={12} lg={3}>
            <Stack spacing={3}>
              <ServiceBlock title={colB[0].title} desc={colB[0].desc} />
              <ServiceBlock title={colB[1].title} desc={colB[1].desc} />
            </Stack>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 2,
                overflow: 'visible',
                bgcolor: '#fff',
                position: 'relative'
              }}
            >
              <Grid container>
                <Grid item xs={12} sm={7}>
                  <Box sx={{ p: { xs: 3, md: 4 } }}>
                    <Stack spacing={1.5}>
                      <Box
                        ref={counterWrapRef}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 0,
                          lineHeight: 1
                        }}
                      >
                        {digits.map((_, idx) => (
                          <ReelDigit
                            key={idx}
                            setRef={(node) => {
                              reelRefs.current[idx] = node;
                            }}
                          />
                        ))}
                        <ReelPercent />
                      </Box>

                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Satisfaction rate
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        Based on 350+ reviews
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={99}
                        sx={{
                          mt: 1,
                          height: 10,
                          borderRadius: 999,
                          backgroundColor: 'rgba(67,147,74,0.15)',
                          '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: GREEN_96 }
                        }}
                      />
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={5} sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: 240, sm: 280, md: 320 },
                      overflow: 'visible',
                      display: 'flex',
                      alignItems: 'flex-end',
                      justifyContent: { xs: 'center', sm: 'flex-end' }
                    }}
                  >
                    <Box
                      component="img"
                      src="https://qleen.bold-themes.com/demo-01/wp-content/uploads/sites/2/2025/07/green_glove.png"
                      alt="green glove"
                      sx={{
                        position: 'relative',
                        right: { xs: '50%', sm: 16 },
                        bottom: 0,
                        transform: { xs: 'translateX(50%)', sm: 'none' },
                        width: { xs: 220, sm: 260, md: 278 },
                        height: 'auto',
                        maxWidth: 'none',
                        top: { xs: -10, sm: -40, md: -55 },
                        zIndex: 2,
                        pointerEvents: 'none',
                        userSelect: 'none',
                         overflow: { xs: 'visible', sm: 'hidden' },
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ display: { xs: 'none', md: 'block' }, height: 48 }} />
      </Box>
    </Box>
  );
};

export default AboutSection;
